import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Send, X, Sparkles, Bot, User as UserIcon, Loader2, Minimize2 } from "lucide-react";

/**
 * Hygiena AI — admin-only floating command widget (Phase 54).
 *
 * Hidden from regular members entirely. For founder/admin it pins to the
 * bottom-right of every authenticated page. Closed state is a small "Hygiena"
 * pill. Open state is a 380×520 chat panel.
 *
 * Talks to POST /api/hygiena/command which classifies admin intent and
 * routes to the appropriate audited admin endpoint. Destructive actions
 * (delete content / reset password) require a typed "yes" confirmation.
 */
const HygienaChat = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi — I'm Hygiena, your admin command line. Tell me what to do, e.g. *reset password for member@x.com*, *delete discussion abc123*, *lookup jane@y.com*, *flag social_post pid spam*, or *scan content last 24h*. Destructive actions need a typed **yes** to confirm.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  // When a destructive intent is awaiting confirm, we cache the plan and the
  // next user message that resolves to "yes" executes it.
  const [pendingPlan, setPendingPlan] = useState(null);
  const messagesEndRef = useRef(null);

  // Defence-in-depth — hide for non-admins even if a stray render happens.
  const isAdminUser = !!user && (user.is_admin || user.role === "founder");

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  if (!isAdminUser) return null;

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setLoading(true);

    // Confirmation path: if a pending plan exists, "yes" executes it,
    // anything else cancels it.
    const isYes = /^\s*y(es)?\s*$/i.test(text);
    if (pendingPlan && !isYes) {
      setPendingPlan(null);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Cancelled. No action taken." },
      ]);
      setLoading(false);
      return;
    }

    try {
      const res = await axios.post(`${API}/hygiena/command`, {
        message: text,
        session_id: sessionId,
        confirm: !!pendingPlan && isYes,
        pending: pendingPlan,
      });
      const data = res.data || {};
      setSessionId(data.session_id || sessionId);
      if (data.type === "confirm") {
        setPendingPlan(data.pending || null);
      } else {
        setPendingPlan(null);
      }
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: data.response || "(no response)",
          variant: data.type === "confirm" ? "confirm" : data.ok === false ? "warn" : "ok",
        },
      ]);
    } catch (e) {
      const detail = e.response?.data?.detail || "Hygiena couldn't respond. Try again.";
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: detail, variant: "warn" },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const quickActions = [
    "scan content last 24h",
    "lookup admin@dhdh.ca",
    "show recent admin actions",
    "help",
  ];

  return (
    <>
      {/* Minimized pill (closed state) */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-6 right-6 z-50 inline-flex items-center gap-2 pl-3 pr-4 py-2.5 rounded-full bg-[#6B4E8A] text-white shadow-lg hover:bg-[#553D6F] transition-colors"
            data-testid="hygiena-chat-btn"
            aria-label="Open Hygiena admin command widget"
          >
            <span className="w-7 h-7 rounded-full bg-white/15 flex items-center justify-center">
              <Sparkles className="w-4 h-4" />
            </span>
            <span className="text-sm font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>Hygiena</span>
            <span className="text-[10px] uppercase tracking-wider text-white/60 hidden sm:inline">admin</span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat panel (open state) */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-24px)] h-[520px] max-h-[calc(100vh-24px)] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-[#E5DFEC]"
            data-testid="hygiena-chat-window"
          >
            {/* Header */}
            <div className="bg-[#6B4E8A] p-4 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center">
                  <Sparkles className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="font-medium leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>Hygiena</h3>
                  <p className="text-[11px] text-white/70 leading-tight">Admin command line · {user.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 hover:bg-white/15 rounded-full transition-colors"
                  aria-label="Minimize Hygiena"
                  data-testid="hygiena-minimize-btn"
                  title="Minimize"
                >
                  <Minimize2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 hover:bg-white/15 rounded-full transition-colors"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#FAF7F2]" data-testid="hygiena-messages">
              {messages.map((message, idx) => (
                <motion.div
                  key={message.id || `msg-${idx}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div className={`flex items-start gap-2 max-w-[85%] ${message.role === "user" ? "flex-row-reverse" : ""}`}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                      message.role === "user" ? "bg-[#6B4E8A] text-white" : "bg-[#8DB5A0] text-white"
                    }`}>
                      {message.role === "user" ? <UserIcon className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5" />}
                    </div>
                    <div className={`p-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                      message.role === "user"
                        ? "bg-[#6B4E8A] text-white rounded-br-sm"
                        : message.variant === "warn"
                          ? "bg-[#FEF2F2] border border-[#FCA5A5] text-[#7F1D1D] rounded-bl-sm"
                          : message.variant === "confirm"
                            ? "bg-[#FFFBEB] border border-[#FDE68A] text-[#78350F] rounded-bl-sm"
                            : "bg-white border border-[#E5DFEC] text-[#6B4E8A] rounded-bl-sm"
                    }`}>
                      {message.content}
                    </div>
                  </div>
                </motion.div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-[#8DB5A0] flex items-center justify-center">
                      <Bot className="w-3.5 h-3.5 text-white" />
                    </div>
                    <div className="bg-white border border-[#E5DFEC] rounded-2xl rounded-bl-sm p-3">
                      <Loader2 className="w-4 h-4 animate-spin text-[#8DB5A0]" />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Quick actions (only initial) */}
            {messages.length === 1 && (
              <div className="p-3 border-t border-[#E5DFEC] bg-white">
                <p className="text-[11px] text-[#64748B] mb-2">Try:</p>
                <div className="flex flex-wrap gap-1.5">
                  {quickActions.map((action, idx) => (
                    <button
                      key={action}
                      onClick={() => {
                        setInput(action);
                        setTimeout(() => handleSend(), 50);
                      }}
                      className="text-[11px] px-2.5 py-1 bg-[#F0ECF7] text-[#6B4E8A] rounded-full hover:bg-[#E5DFEC] transition-colors"
                      data-testid={`hygiena-quick-${idx}`}
                    >
                      {action}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <div className="p-3 border-t border-[#E5DFEC] bg-white">
              {pendingPlan && (
                <p className="text-[11px] text-[#D97706] mb-2 px-1">
                  ⏳ Awaiting confirmation — type <strong>yes</strong> to execute, or anything else to cancel.
                </p>
              )}
              <div className="flex gap-2">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder={pendingPlan ? "Type 'yes' to confirm…" : "Tell Hygiena what to do…"}
                  className="flex-1 bg-[#FAF7F2] border-[#E5DFEC] focus:border-[#8DB5A0] text-sm"
                  disabled={loading}
                  data-testid="hygiena-input"
                />
                <Button
                  onClick={handleSend}
                  disabled={loading || !input.trim()}
                  className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full px-4"
                  data-testid="hygiena-send-btn"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default HygienaChat;
